import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  computed,
  effect,
  input,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import '@maersk-global/mds-components-core/mc-table';
import '@maersk-global/mds-components-core/mc-pagination';
import '@maersk-global/mds-components-core/mc-select';
import { TableColumn } from '@maersk-global/mds-components-core/mc-table/types';
import type { IMcTableSortChangeDetail } from '@maersk-global/mds-components-core/mc-table/types';
import { scoreBand } from '../../../helper/vsc-score-band';
import { VscListRow } from '../../../models/vsc-gsc-view.model';

type TableRow = {
  id: string;
  vendorCode: string;
  vendorName: string;
  country: string;
  workOrderCount: number | null;
  overallScore: number | null;
  leadTimeScore: number | null;
  processComplianceScore: number | null;
  eEIRScore: number | null;
  auditResultScore: number | null;
};

@Component({
  selector: 'app-vsc-grid',
  imports: [CommonModule, RouterLink],
  templateUrl: './vsc-grid.component.html',
  styleUrl: './vsc-grid.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class VscGridComponent {
  readonly rows = input<VscListRow[]>([]);
  /** Month currently selected on the list view, e.g. "Mar 2026". Carried to the detail view so both stay in sync. */
  readonly month = input<string>('');

  readonly linkQueryParams = computed(() => (this.month() ? { month: this.month() } : {}));

  readonly sortState = signal<{ columnId: string; direction: 'ascending' | 'descending' } | null>(null);
  readonly currentPage = signal(1);
  readonly pageSize = signal(20);

  readonly sortedRows = computed(() => {
    const state = this.sortState();
    if (!state) return this.rows();
    return this.sortByColumn(this.rows(), state.columnId, state.direction);
  });

  readonly totalPages = computed(() => Math.ceil(this.rows().length / this.pageSize()) || 1);

  readonly tableData = computed<TableRow[]>(() => {
    const size = this.pageSize();
    const page = this.currentPage();
    return this.sortedRows()
      .slice((page - 1) * size, page * size)
      .map((r) => ({
        id: r.vendorCode,
        vendorCode: r.vendorCode,
        vendorName: r.vendorName,
        country: r.country,
        workOrderCount: r.workOrderCount,
        overallScore: r.overallScore,
        leadTimeScore: r.leadTimeScore,
        processComplianceScore: r.processComplianceScore,
        eEIRScore: r.eEIRScore,
        auditResultScore: r.auditResultScore,
      }));
  });

  readonly pageRangeLabel = computed(() => {
    const total = this.rows().length;
    if (total === 0) return '';
    return `Showing ${this.tableData().length} of ${total}`;
  });

  readonly columns: TableColumn[] = [
    { id: 'vendorCode', label: 'Vendor Code', sorter: () => 0 },
    { id: 'vendorName', label: 'Vendor Name', sorter: () => 0 },
    { id: 'country', label: 'Country', sorter: () => 0 },
    { id: 'workOrderCount', label: 'Work Orders', sorter: () => 0 },
    { id: 'overallScore', label: 'Overall Score', sorter: () => 0 },
    { id: 'leadTimeScore', label: 'Lead Time', sorter: () => 0 },
    { id: 'processComplianceScore', label: 'Process Compliance', sorter: () => 0 },
    { id: 'eEIRScore', label: 'eEIR', sorter: () => 0 },
    { id: 'auditResultScore', label: 'Audit Result', sorter: () => 0 },
  ];

  constructor() {
    effect(
      () => {
        this.rows(); // reset to page 1 whenever the row set changes
        this.currentPage.set(1);
      },
      { allowSignalWrites: true },
    );
  }

  onPageChange(event: Event): void {
    this.currentPage.set((event as CustomEvent<number>).detail ?? 1);
  }

  onPageSizeChange(event: Event): void {
    const size = (event as CustomEvent<{ value?: number }>).detail?.value ?? 20;
    this.pageSize.set(size);
    this.currentPage.set(1);
  }

  onSortChange(event: Event): void {
    const { column, direction } = (event as CustomEvent<IMcTableSortChangeDetail>).detail;
    this.sortState.set({ columnId: column, direction });
    this.currentPage.set(1);
  }

  /** Delegates to the shared helper so the grid and the Excel export shade a score identically. */
  scoreBand(v: number | null): string {
    return scoreBand(v);
  }

  scoreLabel(v: number | null): string {
    return v == null ? '—' : String(v);
  }

  /** 1025 -> 1K, 1550 -> 1.6K, 2400000 -> 2.4M */
  countLabel(v: number | null): string {
    if (v == null) return '—';
    const abs = Math.abs(v);
    if (abs < 1000) return String(v);
    const [divisor, unit] = abs < 1_000_000 ? [1000, 'K'] : [1_000_000, 'M'];
    const scaled = v / divisor;
    const rounded = Math.round(scaled * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)}${unit}`;
  }

  private sortByColumn(data: VscListRow[], columnId: string, direction: 'ascending' | 'descending'): VscListRow[] {
    const dir = direction === 'ascending' ? 1 : -1;
    return [...data].sort((a, b) => {
      const av = (a as unknown as Record<string, unknown>)[columnId];
      const bv = (b as unknown as Record<string, unknown>)[columnId];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      const aStr = (av as string).trim().toLowerCase();
      const bStr = (bv as string).trim().toLowerCase();
      let cmp = 0;
      if (aStr < bStr) cmp = -1;
      else if (aStr > bStr) cmp = 1;
      return cmp * dir;
    });
  }
}
